import React, { useEffect, useRef, useState } from "react";
import { MapTo } from "@adobe/aem-react-editable-components";
import { CardCarouselProps, CardData } from "./CardCarousel.types";
import {
	RangoCarouselNav,
	RangoHeading,
	RangoIcon,
	RangoLinkIcon,
	RangoParagraph,
} from "@rango/core";
import { withRangoContainer } from "../../../../utils/js/rangoContainer";
import { useIsMobile } from "../../../../utils/js/useIsMobile";
import {
	CardCarouselContainer,
	CardInfoContainer,
	CardListItem,
	CardsWrapper,
} from "./CardCarousel.styles";
import Slider from "react-slick";
import sanitizeHtml from "sanitize-html";
import sanitizeHtmlWhitelist from "src/components/sanitize-html.whitelist";
import { useAleloContext } from "src/context/aleloContext";

export default function CardCarousel({
	title,
	cardList,
	rangoContainer,
	id,
}: CardCarouselProps) {
	const { setCurrentModal } = useAleloContext();
	const { isMobile, isTablet } = useIsMobile();
	const [currentIndex, setCurrentIndex] = useState(1);
	const cardSliderRef = useRef<Slider | null>(null);
	const cardTotalItems = cardList ? cardList.length : 0;

	useEffect(() => {
		if (cardSliderRef.current && currentIndex > 0) {
			cardSliderRef.current.slickGoTo(currentIndex - 1);
		}
	}, [currentIndex]);

	function openUrl(linkUrl: string, target: string) {
		window.open(linkUrl, target, "noreferrer");
		return null;
	}

	const tags = (
		dataParent: string,
		actionType: string,
		linkUrl: string,
		modalTarget: string,
		buttonText: string
	) => ({
		element_parent: dataParent || "information-card",
		element_type: actionType === "link" ? "link" : "button",
		destination: actionType === "link" ? linkUrl || "" : modalTarget || "",
		title: buttonText,
	});

	const cardSliderSettings = {
		dots: false,
		swipe: false,
		draggable: false,
		responsive: [
			{
				breakpoint: 1024,
				settings: {
					dots: cardTotalItems > 1,
					swipe: cardTotalItems > 1,
					draggable: cardTotalItems > 1,
				},
			},
		],
		infinite: false,
		className: "card-slider",
		arrows: false,
		beforeChange: (_: number, next: number) => setCurrentIndex(next + 1),
	};

	const renderCard = (card: CardData, k: number) => (
		<CardListItem key={k}>
			{card.topicsList && card.topicsList.length > 0 && (
				<CardInfoContainer>
					<div>
						<div
							className={`rango-row topics-icons-card ${cardTotalItems === 1 ? "single-card" : ""
								}`}
						>
							<div
								className="rango-col-6 card-div"
								style={{ background: card.cardBackgroundColor }}
							>
								{card.backgroundImage != undefined && card.backgroundImage != "" ?
									(<img
										className={`topics-icons-img ${card.imgFit} ${card.columns}`}
										src={card.backgroundImage}
										alt={card.alt}
										style={{ background: card.backgroundImageColor }}
										loading="lazy"
									/>
									) : null
								}
								<div className="card-text">
									<RangoHeading
										size="md"
										tag="h2"
										style={{
											color: card.cardTitleColor,
											marginBottom: card.cardSubtitle ? "0px" : "1em",
										}}
									>
										{card.cardTitle}
									</RangoHeading>
									{card.cardSubtitle ? (
										<RangoParagraph
											style={{
												color: "#333333",
												marginBottom: "8px",
												width: "100%",
											}}
											dangerouslySetInnerHTML={{
												__html: sanitizeHtml(
													card.cardSubtitle,
													sanitizeHtmlWhitelist
												),
											}}
										/>
									) : null}
									<div className={`rango-col-6 topics-card ${card.columns}`}>
										{card.topicsList &&
											card.topicsList.map(
												({ topicIcon, topicText, topicIconColor }) => (
													<div className="icon-topic-div ">
														<RangoIcon
															icon={topicIcon}
															style={{
																maxWidth: "fit-content",
																color: topicIconColor,
															}}
														/>
														<RangoParagraph size="sm" children={topicText} />
													</div>
												)
											)}
									</div>
									<div className="button-div">
										<RangoLinkIcon
											aria-label={card.buttonText}
											{...tags(
												card.dataParent,
												card.actionType,
												card.linkUrl,
												card.modalTarget,
												card.buttonText
											)}
											style={{
												backgroundColor: card.backgroundColor,
												color: card.colorButton,
												padding: "8px",
											}}
											href={card.actionType === "link" ? card.linkUrl : "#" + card.modalTarget}
											target={card.target}
											onClick={() =>
												card.actionType === "link"
													? openUrl(card.linkUrl, card.target)
													: setCurrentModal({
														modal: card.modalTarget,
														step: "",
													})
											}
										>
											{card.buttonText}
										</RangoLinkIcon>
									</div>
								</div>
							</div>
						</div>
					</div>
				</CardInfoContainer>
			)}
		</CardListItem>
	);

	const CardCarouselComponent = (
		<CardCarouselContainer id={id}>
			{title ? (
				<RangoHeading
					tag={"h2"}
					style={{
						textAlign: "center",
						fontSize: isMobile
							? "32px"
							: isTablet
								? "var(--size-4x)"
								: "var(--size-6x)",
						fontWeight: isMobile
							? "var(--font-weight-medium)"
							: "var(--font-weight-bold)",
						height: "130px",
						alignContent: "center",
						marginTop: "32px",
					}}
				>
					{title}
				</RangoHeading>
			) : null}
			<CardsWrapper>
				{cardTotalItems > 1 ? (
					<>
						<Slider ref={cardSliderRef} {...cardSliderSettings}>
							{cardList?.map((card, k) => renderCard(card, k))}
						</Slider>
						<div className="card-list_nav">
							<div className="card-list_nav_arrows">
								<RangoCarouselNav
									prevButtonProps={{
										icon: "BackLine",
										title: 'Slide anterior',
										destination: 'prev slide',
										element_parent: 'card-carousel',
										element_type: 'button-prev-slide'
									}}
									nextButtonProps={{
										icon: "GoLine",
										title: 'Próximo slide',
										destination: 'next slide',
										element_parent: 'card-carousel',
										element_type: 'button-next-slide'
									}}
									children={`${currentIndex}/${cardTotalItems}`}
									active={currentIndex}
									items={cardTotalItems}
									onChange={(active: number) => {
										setCurrentIndex(active);
									}}
								/>
							</div>
						</div>
					</>
				) : cardTotalItems === 1 ? (
					renderCard(cardList[0], 1)
				) : null}
			</CardsWrapper>
		</CardCarouselContainer>
	);
	return withRangoContainer(rangoContainer, CardCarouselComponent);
}

const CardCarouselConfig = {
	emptyLabel: "Carrossel de Cards",

	isEmpty: function (props: CardCarouselProps) {
		return !props || !props.cardList;
	},
};

MapTo<CardCarouselProps>("alelo/components/content/v1/card-carousel")(
	CardCarousel,
	CardCarouselConfig
);
